import { Dialog, Transition } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Fragment, useState } from "react";
import { toast } from "sonner";
import { useAnalytics } from "../lib/analytics";
import { api } from "../lib/api";
import { useClientTranslations } from "../lib/i18n/client";
import styles from "./BetaFeedbackModal.module.css";

interface BetaFeedbackModalProps {
	isOpen: boolean;
	onClose: () => void;
}

export function BetaFeedbackModal({ isOpen, onClose }: BetaFeedbackModalProps) {
	const [feedback, setFeedback] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const analytics = useAnalytics();
	const { t, isRTL } = useClientTranslations("feedback");

	const handleCancel = (source: "cancel_button" | "x_button" | "overlay") => {
		analytics.logEvent("beta_feedback_cancelled", undefined, {
			source,
			had_text: feedback.length > 0 ? "true" : "false",
		});
		setFeedback("");
		onClose();
	};

	const handleTextareaFocus = () => {
		analytics.logEvent("beta_feedback_textarea_focused");
	};

	const handleEmailClick = () => {
		analytics.logEvent("beta_feedback_email_clicked");
	};

	const handleSubmit = async () => {
		if (!feedback.trim()) return;

		setIsSubmitting(true);
		try {
			await api.post("/api/feedback", {
				rating: 0,
				feedback,
				messageId: null,
				chatId: null,
				feedbackType: "beta",
			});

			analytics.logEvent("beta_feedback_submitted");
			analytics.logEvent("beta_feedback_text_length", feedback.length);

			toast.success(t("toast.success"), {
				duration: 3000,
			});

			setFeedback("");
			onClose();
		} catch (error) {
			console.error("Error submitting feedback:", error);
			toast.error(t("toast.error"));
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<Transition appear show={isOpen} as={Fragment}>
			<Dialog
				as="div"
				className="relative"
				onClose={() => handleCancel("overlay")}
			>
				<Transition.Child
					as={Fragment}
					enter="ease-out duration-300"
					enterFrom="opacity-0"
					enterTo="opacity-100"
					leave="ease-in duration-200"
					leaveFrom="opacity-100"
					leaveTo="opacity-0"
				>
					<div className={styles.overlay} />
				</Transition.Child>

				<div className={styles.container}>
					<div className={styles.centered}>
						<Transition.Child
							as={Fragment}
							enter="ease-out duration-300"
							enterFrom="opacity-0 scale-95"
							enterTo="opacity-100 scale-100"
							leave="ease-in duration-200"
							leaveFrom="opacity-100 scale-100"
							leaveTo="opacity-0 scale-95"
						>
							<Dialog.Panel
								className={styles.panel}
								style={{
									direction: isRTL ? "rtl" : "ltr",
								}}
							>
								{/* Gradient border */}
								<div className={styles.gradientBorder}>
									<div className={styles.gradientBorderInner} />
								</div>
								{/* Beta tag in top left */}
								<div className={styles.betaBadge}>
									<span className={styles.betaBadgeText}>Beta</span>
								</div>

								{/* Close button */}
								<button
									type="button"
									className={styles.closeButton}
									onClick={() => handleCancel("x_button")}
								>
									<XMarkIcon className={styles.closeIcon} />
								</button>

								<div className={styles.content}>
									<p className={styles.title}>{t("beta.title1")}</p>
									<p className={`${styles.title} ${styles.titleMargin}`}>
										{t("beta.title2")}
									</p>

									<div className={styles.textareaContainer}>
										<textarea
											value={feedback}
											onChange={(e) => setFeedback(e.target.value)}
											onFocus={handleTextareaFocus}
											className={styles.textarea}
											placeholder={t("beta.placeholder")}
											rows={5}
											dir={isRTL ? "rtl" : "ltr"}
										/>
									</div>

									<div className={styles.buttons}>
										<button
											type="button"
											className={styles.cancelButton}
											onClick={() => handleCancel("cancel_button")}
											disabled={isSubmitting}
										>
											{t("buttons.cancel")}
										</button>
										<button
											type="button"
											className={styles.submitButton}
											onClick={handleSubmit}
											disabled={!feedback.trim() || isSubmitting}
										>
											{isSubmitting
												? t("buttons.submitting")
												: t("buttons.submit")}
										</button>
									</div>

									<p className={styles.footer}>
										{t("beta.support")}{" "}
										<a
											href="mailto:support@eraborn.co"
											target="_blank"
											rel="noopener noreferrer"
											className={styles.footerLink}
											onClick={handleEmailClick}
										>
											support@eraborn.co
										</a>
									</p>
								</div>
							</Dialog.Panel>
						</Transition.Child>
					</div>
				</div>
			</Dialog>
		</Transition>
	);
}
